/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: add, edit, duplicate and remove workouts, shared by every planner.
 */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from "@lemonadetraining/web-ui";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";
import WorkoutEditor from "../workout/WorkoutEditor";
import type { PlanData, PlannedWorkout } from "../../../hooks/useProgramWorkouts";

/** Where a workout sits: a date, a week (and maybe a day), or nowhere in particular. */
export interface Placement {
  date?: Date | null;
  week?: number;
  day?: number | null;
}

type Workouts = {
  create: (data: PlanData) => Promise<PlannedWorkout>;
  update: (workout: PlannedWorkout, data: PlanData) => Promise<PlannedWorkout>;
  remove: (workout: PlannedWorkout) => Promise<void>;
};

export interface PlannerEditingOptions {
  programName: string;
  workouts: Workouts;
  describe: (placement: Placement) => string;
  /** Private notes, for people who manage the program; absent otherwise. */
  notes?: {
    label: string;
    /** Notes can be edited only once they have loaded, or an unseen note would be overwritten. */
    status: "idle" | "loading" | "ready" | "error";
    byId: Record<string, string>;
    save: (workoutID: string, text: string) => Promise<void>;
  };
  /** A control that changes the placement while editing ("Move to…"); absent for lists. */
  renderPlacementControl?: (
    placement: Placement,
    onChange: (next: Placement) => void
  ) => React.ReactNode;
}

const blankWorkout = (placement: Placement): PlanData => ({
  title: "",
  description: "",
  tags: [],
  youTubeIDs: [],
  movements: [],
  ...placementFields(placement),
});

const placementFields = (placement: Placement): Partial<PlanData> => {
  const fields: Partial<PlanData> = {};
  if (placement.date !== undefined) fields.date = placement.date;
  if (placement.week !== undefined) fields.week = placement.week;
  if (placement.day !== undefined) fields.day = placement.day;
  return fields;
};

const placementOf = (data: PlanData): Placement => ({
  ...(data.date !== undefined ? { date: data.date ?? null } : {}),
  ...(typeof data.week === "number" ? { week: data.week } : {}),
  ...(data.day !== undefined ? { day: data.day ?? null } : {}),
});

const samePlacement = (a: Placement, b: Placement) =>
  (a.date?.getTime() ?? null) === (b.date?.getTime() ?? null) &&
  (a.week ?? null) === (b.week ?? null) &&
  (a.day ?? null) === (b.day ?? null);

export const copyOfWorkout = (data: PlanData): PlanData => ({
  ...data,
  tags: [...(data.tags ?? [])],
  youTubeIDs: [...(data.youTubeIDs ?? [])],
  movements: (data.movements ?? []).map((m) => ({ ...m })),
});

export function usePlannerEditing({
  programName,
  workouts,
  describe,
  renderPlacementControl,
  notes,
}: PlannerEditingOptions) {
  const dispatch = useDispatch();
  const [editor, setEditor] = useState<{
    open: boolean;
    mode: "create" | "edit";
    initial: PlanData;
    workout: PlannedWorkout | null;
    placement: Placement;
    key: number;
  }>({
    open: false,
    mode: "create",
    initial: blankWorkout({}),
    workout: null,
    placement: {},
    key: 0,
  });
  const [removing, setRemoving] = useState<PlannedWorkout | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const openCreate = (placement: Placement) =>
    setEditor((prev) => ({
      open: true,
      mode: "create",
      initial: blankWorkout(placement),
      workout: null,
      placement,
      key: prev.key + 1,
    }));

  const openEdit = (workout: PlannedWorkout) =>
    setEditor((prev) => ({
      open: true,
      mode: "edit",
      initial: copyOfWorkout(workout.workoutData),
      workout,
      placement: placementOf(workout.workoutData),
      key: prev.key + 1,
    }));

  const save = async (data: PlanData, noteText?: string) => {
    const placed = { ...data, ...placementFields(editor.placement) };
    let saved: PlannedWorkout;
    if (editor.mode === "edit" && editor.workout) {
      saved = await workouts.update(editor.workout, placed);
    } else {
      saved = await workouts.create(placed);
      // From here a retry must update this workout, never create a second one.
      setEditor((prev) => ({ ...prev, mode: "edit", workout: saved }));
    }
    if (!notes || notes.status !== "ready" || noteText === undefined) return;
    if (noteText.trim() === (notes.byId[saved.id] ?? "")) return;
    try {
      await notes.save(saved.id, noteText);
    } catch (error: unknown) {
      console.error("Error saving the private note:", error);
      const denied = /permission-denied/.test(String((error as { code?: string })?.code ?? ""));
      throw Object.assign(new Error("note not saved"), {
        userMessage: denied
          ? "The workout is saved. You can't change private notes on this program."
          : "The workout is saved, but the private note isn't. Press Save to try the note again.",
      });
    }
  };

  const duplicate = async (workout: PlannedWorkout) => {
    try {
      const copy = await workouts.create(copyOfWorkout(workout.workoutData));
      const note = notes?.status === "ready" ? notes.byId[workout.id] : undefined;
      // The copy's note is a courtesy: the workout itself is what matters.
      if (note) await notes?.save(copy.id, note).catch(() => undefined);
      dispatch(
        notifyActions.sendNotification({
          severity: NOTIFICATION_TYPE.success,
          title: "Workout duplicated",
          message: "",
        })
      );
    } catch (error: unknown) {
      console.error("Error duplicating the workout:", error);
      dispatch(
        notifyActions.sendNotification({
          severity: NOTIFICATION_TYPE.error,
          title: "Couldn't duplicate the workout",
          message: "Nothing changed. Try again.",
        })
      );
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await workouts.remove(removing);
      setRemoving(null);
    } catch (error: unknown) {
      console.error("Error removing the workout:", error);
      setRemoveError("We couldn't remove it. Nothing changed. Try again.");
    } finally {
      setRemoveBusy(false);
    }
  };

  const nodes = (
    <>
      <WorkoutEditor
        key={editor.key}
        open={editor.open}
        onOpenChange={(open) => setEditor((prev) => ({ ...prev, open }))}
        mode={editor.mode}
        initial={editor.initial}
        placement={describe(editor.placement)}
        placementControl={
          editor.mode === "edit" && renderPlacementControl
            ? renderPlacementControl(editor.placement, (next) =>
                setEditor((prev) => ({ ...prev, placement: next }))
              )
            : undefined
        }
        programName={programName}
        note={
          notes
            ? {
                label: notes.label,
                status: notes.status,
                initial: editor.workout ? (notes.byId[editor.workout.id] ?? "") : "",
              }
            : undefined
        }
        placementChanged={
          editor.mode === "edit" && !samePlacement(editor.placement, placementOf(editor.initial))
        }
        onSave={save}
      />
      <Dialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open && !removeBusy) {
            setRemoving(null);
            setRemoveError(null);
          }
        }}
      >
        <DialogContent data-testid="remove-workout-dialog">
          <DialogHeader>
            <DialogTitle>
              Remove &ldquo;{removing?.workoutData.title || "this workout"}&rdquo;?
            </DialogTitle>
            <DialogDescription>
              Members won&apos;t see it in this program anymore. Results they already logged are
              kept.
            </DialogDescription>
          </DialogHeader>
          {removeError && (
            <p role="alert" className="text-sm text-destructive">
              {removeError}
            </p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRemoving(null)} disabled={removeBusy}>
              Keep it
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemove}
              disabled={removeBusy}
              data-testid="confirm-remove-workout"
            >
              {removeBusy && <Spinner className="mr-2 size-4" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return {
    openCreate,
    openEdit,
    duplicate,
    requestRemove: (workout: PlannedWorkout) => {
      setRemoveError(null);
      setRemoving(workout);
    },
    nodes,
  };
}
